// src/pages/Generate.tsx
import { useEffect, useMemo, useState } from "react";
import UserAvatar from "../components/UserAvatar";
import LanguageSwitcher from "../components/LanguageSwitcher";
import NavBar from "../components/NavBar";
import MobileNav from "../components/MobileNav";

import { dict, getInitialLang, type Lang } from "../lib/i18n";
import {
  getTG,
  initTG,
  getAvatarUrl,
  getUserName,
  vibrate,
  vibrateTick,
  vibrateSuccess,
} from "../lib/telegram";
import { generateImage, type GalleryItem } from "../lib/api";

export default function Generate() {
  const [lang, setLang] = useState<Lang>(() =>
    getInitialLang(getTG()?.initDataUnsafe?.user?.language_code ?? null)
  );

  const tg = useMemo(() => initTG(), []);
  const tgId = tg?.initDataUnsafe?.user?.id ?? null;
  const name = getUserName();
  const avatar = getAvatarUrl();

  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastImage, setLastImage] = useState<GalleryItem | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);


  const toggleLang = () => {
    setLang((l) => {
      const next: Lang = l === "ru" ? "uz" : "ru";
      localStorage.setItem("lang", next);
      return next;
    });
  };

const handleGenerate = async (e?: React.FormEvent) => {
  e?.preventDefault();          // ← фикс формы

  const value = prompt.trim();
  if (!value || !tgId || loading) return;

  setLoading(true);
  setError(null);

  try {
    vibrate();

    const res = await generateImage(tgId, value);

    // нормализуем
    const img = (res as any).image ?? res;

    if (img && img.id) {
      setLastImage(img);
      setShowCelebration(true);
      vibrateSuccess();

      setPrompt("");            // ← очищаем только ПОСЛЕ результата
    } else {
      setError("Не удалось получить изображение от сервера");
    }
  } catch (e: any) {
    console.error(e);
    setError(e?.message || "Ошибка генерации");
  } finally {
    setLoading(false);
  }
};



  useEffect(() => {
    // если зайдём на страницу из Telegram без tgId — просто показываем CTA
  }, [tgId]);

  return (
      <div className="min-h-screen-svh bg-grid relative text-white overscroll-contain">
        <div className="absolute inset-0 grid-lines pointer-events-none"/>
        <div
            className="
      absolute left-1/2 -translate-x-1/2
      z-20
      top-[calc(env(safe-area-inset-top))]
    "
        >
          <div
              className="
          inline-flex items-center gap-2 text-[10px] md:text-xs
          font-semibold px-3 py-1 rounded-full glass
        "
          >
            <span className="size-2 rounded-full bg-emerald-400"></span>
             {lang === "ru" ? "Генерация изображений" : "Rasm generatsiyasi"}
          </div>
        </div>

        <header
            className="
        tg-safe-header
        relative z-10 max-w-6xl mx-auto
        h-12 md:h-16 flex items-center justify-between
        px-3 md:px-4
      "
        >
          <div className="flex items-center gap-2">
            <UserAvatar name={name} src={avatar ?? undefined}/>
            <span className="text-xs md:text-sm text-white/70">
      {dict[lang].welcome} {name}
    </span>
          </div>

          <div className="hidden md:flex">
            <NavBar/>
          </div>

          <div className="flex items-center gap-2">
            <LanguageSwitcher value={lang} onToggle={toggleLang}/>
            {/*<a className="text-xs md:text-sm text-white/70 hover:text-white transition"*/}
            {/*   href="https://github.com/lexaowner" target="_blank" rel="noreferrer">GitHub</a>*/}
            {/*<a className="text-xs md:text-sm text-white/70 hover:text-white transition" href="https://t.me/EMWCL"*/}
            {/*   target="_blank" rel="noreferrer">TG</a>*/}
          </div>
        </header>

        {/* mobile nav снизу / как на остальных страницах */}
        <MobileNav>
          <NavBar compact/>
        </MobileNav>

        {/* MAIN */}
        <main className="relative z-10 max-w-6xl mx-auto px-3 md:px-4 pt-5 md:pt-4">
          {/* HERO / заголовок */}
          <div className="text-center space-y-2 mb-6">

            <p className="text-white/60 text-xs md:text-sm max-w-md mx-auto">
              {lang === "ru"
                  ? "Все сгенерированные работы автоматически сохраняются в разделе «Моя галерея»."
                  : "Barcha yaratilgan rasmlar «Mening galereyam» bo‘limida saqlanadi."}
            </p>
          </div>

          {/* Форма генерации */}
          <section className="glass rounded-2xl md:rounded-3xl p-3 md:p-5 mb-5 space-y-3">
            {!tgId && (
                <div className="text-center text-white/70 text-sm mb-2">
                  Чтобы генерировать изображения, откройте мини-приложение внутри Telegram.
                </div>
            )}

            <form onSubmit={handleGenerate}
                className="flex flex-col gap-3 md:gap-4"
            >
            <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                placeholder={
                  lang === "ru"
                      ? "Например: «Киберпанк Ташкент ночью, неон, стиль аниме»…"
                      : "Masalan: «Kiberpunk Toshkent tunida, neon, anime uslubida»…"
                }
                disabled={!tgId || loading}
            />

              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-3">
                <button
                    type="submit"
                    disabled={!tgId || loading || !prompt.trim()}
                    className="md:w-48 rounded-xl px-4 py-2.5 text-sm font-semibold
                           bg-gradient-to-r from-indigo-500 to-fuchsia-500
                           disabled:opacity-50 hover:opacity-90 transition shadow-md
                           flex items-center justify-center gap-2"
                >
                  {loading
                      ? lang === "ru"
                          ? "Генерирую…"
                          : "Generatsiya…"
                      : lang === "ru"
                          ? "Сгенерировать"
                          : "Generatsiya qilish"}
                </button>

                <div className="flex-1 flex flex-wrap gap-1">
                  {[
                    "футуристический Ташкент ночью, неон",
                    "лего-фигура программиста за компьютером",
                    "логотип пиццерии в минималистичном стиле",
                  ].map((ex) => (
                      <button
                          key={ex}
                          type="button"
                          onClick={() => setPrompt(ex)}
                          className="text-[10px] md:text-xs px-2 py-1 rounded-full
                               bg-white/5 border border-white/10
                               text-white/60 hover:text-white hover:bg-white/10"
                      >
                        {ex}
                      </button>
                  ))}
                </div>
              </div>

              {error && (
                  <p className="text-xs text-red-400 mt-1">
                    {error}
                  </p>
              )}

              {tgId && (
                  <p className="text-[10px] text-white/40">
                    id: {tgId}
                  </p>
              )}
            </form>
          </section>

          {/* Превью последнего изображения */}
          {lastImage && (
              <section className="glass rounded-2xl md:rounded-3xl p-3 md:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm md:text-base font-semibold">
                    {lang === "ru" ? "Последняя работа" : "Oxirgi rasm"}
                  </h2>
                  <a
                      href="#/gallery"
                      className="text-xs text-indigo-300 hover:text-indigo-200"
                  >
                    {lang === "ru"
                        ? "Открыть «Моя галерея» →"
                        : "Galereyaga o'tish →"}
                  </a>
                </div>
                <div className="rounded-xl overflow-hidden bg-white/5 border border-white/10">
                  <img
                      src={lastImage.image_url || ""}
                      alt={lastImage.prompt}
                      className="w-full h-auto object-cover"
                  />
                  <div className="p-3 space-y-2">
                    <p className="text-sm text-white/80">{lastImage.prompt}</p>
                    <div className="flex items-center justify-between text-xs text-white/60">
                      <span>❤️ {lastImage.likes}</span>
                      <button
                          onClick={() => {
                            navigator.clipboard.writeText(lastImage.prompt);
                            vibrateTick();
                          }}
                          className="text-xs hover:text-white"
                      >
                        {lang === "ru"
                            ? "Копировать промпт"
                            : "Promptni nusxalash"}
                      </button>
                    </div>
                  </div>
                </div>
              </section>
          )}

          {/*{!lastImage && (*/}
          {/*  <p className="mt-2 text-center text-xs text-white/50">*/}
          {/*    {lang === "ru"*/}
          {/*      ? "Сгенерируйте первое изображение, и оно появится здесь."*/}
          {/*      : "Birinchi rasmni yarating — u shu yerda chiqadi."}*/}
          {/*  </p>*/}
          {/*)}*/}
        </main>

        {/* CELEBRATION OVERLAY */}
        {showCelebration && lastImage && (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
                onClick={() => setShowCelebration(false)}
            >
              <div
                  className="max-w-sm w-full glass rounded-3xl overflow-hidden border border-indigo-500/60
                       shadow-2xl animate-pop-in"
                  onClick={(e) => e.stopPropagation()}
              >
                <div className="relative">
                  <img
                      src={lastImage.image_url || ""}
                      alt={lastImage.prompt}
                      className="w-full h-auto object-cover"
                  />
                  <div
                      className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/60 via-transparent to-transparent"/>
                  <div className="absolute left-3 bottom-3 flex items-center gap-2">
                <span
                    className="inline-flex items-center justify-center rounded-full bg-emerald-500/90 px-2 py-0.5 text-[11px] font-semibold">
                  ✨ Ready
                </span>
                    <span className="text-xs text-white/80 line-clamp-1 max-w-[180px]">
                  {lastImage.prompt}
                </span>
                  </div>
                </div>
                <div className="p-3 md:p-4 space-y-2">
                  <p className="text-sm md:text-base font-semibold">
                    {lang === "ru" ? "Готово! Картинка сохранена в галерее." : "Tayyor! Rasm galereyaga saqlandi."}
                  </p>
                  <p className="text-xs text-white/60">
                    {lang === "ru"
                        ? "Найти её можно в разделе «Моя галерея»."
                        : "Uni «Mening galereyam» bo‘limida topshingiz mumkin."}
                  </p>
                  <div className="flex gap-2 mt-1">
                    <a
                        href="#/gallery"
                        onClick={() => vibrateTick()}
                        className="flex-1 text-xs md:text-sm px-3 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-center font-semibold transition"
                    >
                      {lang === "ru" ? "Открыть галерею" : "Galereyani ochish"}
                    </a>
                    <button
                        onClick={() => {
                          vibrateTick();
                          setShowCelebration(false);
                        }}
                        className="px-3 py-2 rounded-xl border border-white/20 text-xs md:text-sm text-white/80 hover:bg-white/10 transition"
                    >
                      {lang === "ru" ? "Закрыть" : "Yopish"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
        )}
      </div>
  );
}
